import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { X, Table2, FileSpreadsheet, FileText } from 'lucide-react';
import { CRS, crsOf, recommendCrs, toCrs } from '../lib/crs';
import { download, safeName } from '../lib/exporters';
import { getMode } from '../lib/profiles';
import { getCatalog } from '../lib/catalog';
import { FIX_SHORT } from '../lib/gnss';
import { portalRoot } from './TourGuide';

export const openCoordList = () => window.dispatchEvent(new Event('giss-coord-list'));
const nf = (v, d = 3) => (v == null || Number.isNaN(+v) ? '' : (+v).toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d, useGrouping: false }));
const hOf = (f) => f.gnss?.ground ?? f.gnss?.alt ?? f.elev?.v ?? null;
// one row per surveyed point: number, code, E/N/H in the project CRS, GNSS quality
export function coordRows(project) {
  const crs = crsOf(project), list = (project.fittings || []).filter((f) => f.lat != null);
  const ref = list.length ? [list[0].lat, list[0].lon] : [project.lat || 48, project.lon || 11];
  return { crs, label: CRS[crs].label(ref[0], ref[1]), rows: list.map((f, i) => {
    const [e, n] = toCrs(crs, f.lat, f.lon, ref), g = f.gnss || {};
    return { id: f.id, no: f.pno ?? i + 1, code: f.code || f.sym || '', name: f.name, e, n, h: hOf(f), fix: f.gnss ? FIX_SHORT[g.fix] || 'GNSS' : f.draft ? 'draft' : 'map', hrms: g.hrms ?? g.maxHrms ?? g.acc ?? null, vrms: g.vrms ?? g.maxVrms ?? null };
  }) };
}
export const coordTable = (t, project) => {
  const { rows, label, crs } = coordRows(project), wgs = crs === 'wgs', d = wgs ? 8 : 3;
  return { label, head: [t.clNo, t.clCode, t.clName, wgs ? t.clLon : t.clE, wgs ? t.clLat : t.clN, t.clH, t.clFix, 'HRMS', 'VRMS'], body: rows.map((r) => [r.no, r.code, r.name, nf(r.e, d), nf(r.n, d), nf(r.h), t['clFix_' + r.fix] || r.fix, nf(r.hrms), nf(r.vrms)]), raw: rows };
};
function exportCsv(t, project) {
  const { label, head, body } = coordTable(t, project);
  const csv = [`# ${label}`, head.join(';'), ...body.map((r) => r.map((c) => (/[;"\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : c)).join(';'))].join('\r\n');
  download(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }), `${safeName(project.title)}_Koordinaten.csv`);
}
function exportXlsx(t, project) {
  const { label, head, body, raw } = coordTable(t, project), wb = XLSX.utils.book_new(), k = coordRows(project).crs === 'wgs' ? 1e8 : 1e3, r3 = (v) => (v == null ? '' : Math.round(v * k) / k);
  const data = body.map((b, i) => [b[0], b[1], b[2], r3(raw[i].e), r3(raw[i].n), r3(raw[i].h), b[6], r3(raw[i].hrms), r3(raw[i].vrms)]);
  const ws = XLSX.utils.aoa_to_sheet([[`${t.clTitle} – ${project.title}`], [label], [], head, ...data]);
  Object.keys(ws).filter((k) => /^[D-FHI]\d+$/.test(k) && typeof ws[k].v === 'number').forEach((k) => { ws[k].z = '0.000'; });
  XLSX.utils.book_append_sheet(wb, ws, t.clSheet);
  download(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${safeName(project.title)}_Koordinaten.xlsx`);
}

export default function CoordList({ t, project, updateProject }) {
  const [on, setOn] = useState(false);
  useEffect(() => { const h = () => setOn(true); window.addEventListener('giss-coord-list', h); return () => window.removeEventListener('giss-coord-list', h); }, []);
  if (!on) return null;
  const crs = crsOf(project), rec = recommendCrs(project), { label, head, body } = coordTable(t, project);
  return createPortal(
    <div className="editor-backdrop" data-testid="coord-list-dialog">
      <div className="editor irr-bom coord-list">
        <div className="editor-head"><h4><Table2 size={18} /> {t.clTitle}</h4><button className="icon-btn" data-testid="coord-list-close-btn" onClick={() => setOn(false)}><X size={16} /></button></div>
        <label className="pit-num"><span>{t.clCrs}</span>
          <select data-testid="coord-crs-select" value={crs} onChange={(e) => updateProject((p) => ({ ...p, crs: e.target.value }))}>
            {Object.keys(CRS).map((k) => <option key={k} value={k}>{t['crs_' + k]}{k === rec ? ` – ${t.clRec}` : ''}</option>)}
          </select>
        </label>
        <p className="sw-res" data-testid="coord-crs-label">{label}</p>
        {!CRS[crs].exact && <p className="cover-warn" data-testid="coord-crs-warn">{t.clApprox}</p>}
        {!body.length ? <p className="empty" data-testid="coord-list-empty">{t.clEmpty}</p> : (
          <div className="coord-wrap"><table className="coord-table" data-testid="coord-table"><thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>{body.map((r, i) => <tr key={i} data-testid={`coord-row-${i}`}>{r.map((c, k) => <td key={k} className={k > 2 ? 'mono' : ''}>{c}</td>)}</tr>)}</tbody></table></div>
        )}
        <div className="row">
          <button className="btn btn-green btn-sm" data-testid="coord-xlsx-btn" disabled={!body.length} onClick={() => exportXlsx(t, project)}><FileSpreadsheet size={14} /> Excel</button>
          <button className="btn btn-dark btn-sm" data-testid="coord-csv-btn" disabled={!body.length} onClick={() => exportCsv(t, project)}><FileText size={14} /> CSV</button>
        </div>
      </div>
    </div>,
    portalRoot(),
  );
}

// point number + code of one surveyed point (object sheet)
export function PointIdFields({ t, obj, patch }) {
  if (getMode() !== 'survey') return null;
  return (
    <div className="spray-fields" data-testid="point-id-fields">
      <label>{t.clNo}<input inputMode="numeric" data-testid="point-no-input" value={obj.pno ?? ''} onChange={(e) => patch({ pno: e.target.value.replace(/\D/g, '') ? +e.target.value.replace(/\D/g, '') : null })} /></label>
      <label>{t.clCode}<input data-testid="point-code-input" value={obj.code ?? obj.sym ?? ''} onChange={(e) => patch({ code: e.target.value.toUpperCase().slice(0, 12) })} /></label>
    </div>
  );
}

// map legend in survey mode: codes of the survey catalogue (used ones first, else all)
export function SurveyLegend({ t, project }) {
  const items = getCatalog().items.filter((x) => x.sector === 'survey'), used = new Set([...(project?.fittings || []), ...(project?.pipes || [])].map((x) => x.catId));
  const list = items.some((x) => used.has(x.id)) ? items.filter((x) => used.has(x.id)) : items;
  return (
    <div data-testid="survey-legend">
      <b>{t.cat_svpt} / {t.cat_topo}</b>
      {list.map((x) => (
        <div key={x.id} className="lg-row" data-testid={`survey-legend-${x.id}`}>
          {x.kind === 'pipe' ? <i className="lg-line" style={{ background: x.color }} /> : <span className="sv-code" style={{ background: x.color }}>{x.sym}</span>}{x.name}
        </div>
      ))}
    </div>
  );
}
